import {useEffect, useRef, type SetStateAction} from 'react';
import equal from 'fast-deep-equal';
import {atom, useSetAtom} from 'jotai';
import {executeOrReturnAtomState} from '@netx/core-next/utils';
import {userPrefs} from '@netx/core-user/collections/singletons/userPreferences';
import {setPreference} from '@netx/core-user-next/utils/setPreference';
import {
	DEFAULT_SEARCH_SORT_FIELD,
	DEFAULT_SEARCH_SORT_ORDER,
	DEFAULT_SEARCH_MATCH_CRITERIA,
	SearchSortOrder,
} from '../constants';
import type {SearchCriteria, SearchSort} from '../types';
import {areRulesValid} from '../utils/areRulesValid';
import {areSearchRulesSortable} from '../utils/areSearchRulesSortable';
import {getUserSort} from '../utils/getUserSort';
import {updateUrlSearchString} from '../utils/updateUrlSearchString';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {rulesContainAI} from '../rules/ai/utils/rulesContainAI';
import {rulesContainVisual} from '../rules/visual/utils/rulesContainVisual';
import {rulesContainCustomLatestThing} from '../rules/custom/utils/rulesContainCustomLatestThing';

type SearchRulesState = Omit<SearchCriteria, 'sortField' | 'sortOrder'> & {};

const initialState: SearchRulesState = {
	elementTypes: [],
	elementSubType1s: [],
	elementSubType2s: [],
	elementValue1s: [],
	elementValue2s: [],
	elementValue3s: [],
	matchCriteria: DEFAULT_SEARCH_MATCH_CRITERIA,
};

export type SearchRuleConditions = Partial<SearchCriteria>;

export type UseSearchRulesParams = {
	conditions?: SearchRuleConditions;
	sortFieldPrefKey?: string;
	sortOrderPrefKey?: string;
};

export const useSearchRules = ({
	conditions,
	sortFieldPrefKey,
	sortOrderPrefKey,
}: UseSearchRulesParams = {}) => {
	const searchRulesAtom = useRef(
		atom(initialState, (get, set, value: SetStateAction<SearchRulesState>) => {
			const currentConditions = get(searchRulesAtom.current);
			const conditions = executeOrReturnAtomState(value, currentConditions);
			if (equal(conditions, currentConditions)) return;
			if (!window.location.hash.includes('search/in/advanced/')) {
				// TODO: this should happen in the code that uses this atom
				// console.log(
				// 	'updating url search string with conditions change',
				// 	conditions,
				// );
				updateUrlSearchString({...conditions, ...get(searchSortAtom.current)});
			}
			set(searchRulesAtom.current, conditions);
		}),
	);

	const searchRulesValidAtom = useRef(
		atom((get) => areRulesValid(get(searchRulesAtom.current))),
	);

	const searchSortAtom = useRef(
		atom(
			getUserSort({
				sortFieldPrefKey,
				sortOrderPrefKey,
				// defaultSortField: DEFAULT_SEARCH_SORT_FIELD,
				// defaultSortOrder: DEFAULT_SEARCH_SORT_ORDER,
			}),
			(get, set, value: SetStateAction<SearchSort>) => {
				const currentSort = get(searchSortAtom.current);
				const sort = executeOrReturnAtomState(value, currentSort);
				if (equal(sort, currentSort)) return;

				const conditions = get(searchRulesAtom.current);
				const sortable = areSearchRulesSortable(conditions);
				if (sortable) {
					if (
						sortFieldPrefKey &&
						currentSort.sortField !== 'aiRelevance' &&
						sort.sortField !== 'aiRelevance' &&
						currentSort.sortField !== sort.sortField
					) {
						setPreference(sortFieldPrefKey, sort.sortField);
					}
					if (sortOrderPrefKey && currentSort.sortOrder !== sort.sortOrder) {
						setPreference(sortOrderPrefKey, sort.sortOrder);
					}
				}
				// TODO: this should happen in the code that uses this atom
				if (!window.location.hash.includes('search/in/advanced/')) {
					// console.log('updating url search string with sort change', sort);
					updateUrlSearchString({...conditions, ...sort});
				}
				set(searchSortAtom.current, sort);
			},
		),
	);

	const setSearchRules = useSetAtom(searchRulesAtom.current);
	const setSearchSort = useSetAtom(searchSortAtom.current);

	const _getCorrectedSortField = useConstCallback(
		(sortField: string, conditions: SearchRuleConditions) => {
			if (Object.keys(conditions).length === 0) return sortField;
			if (rulesContainCustomLatestThing(conditions)) return 'importDate';
			if (
				(sortField === 'aiRelevance' &&
					!rulesContainAI(conditions) &&
					!rulesContainVisual(conditions)) ||
				(sortField === 'importDate' &&
					!rulesContainCustomLatestThing(conditions))
			) {
				return (
					getUserSort({sortFieldPrefKey, sortOrderPrefKey})?.sortField ??
					DEFAULT_SEARCH_SORT_FIELD
				);
			}
			return sortField;
		},
	);

	const _getCorrectedSortOrder = useConstCallback(
		(
			sortOrder: SearchSort['sortOrder'] | undefined,
			conditions: SearchRuleConditions,
		) => {
			if (Object.keys(conditions).length === 0) return sortOrder;
			if (rulesContainCustomLatestThing(conditions)) {
				return SearchSortOrder.DESC;
			} else {
				return (
					sortOrder ??
					getUserSort({sortFieldPrefKey, sortOrderPrefKey})?.sortOrder
				);
			}
		},
	);

	const updateSearchRulesWithoutChangingPreferences = useConstCallback(
		({sortField, sortOrder, ...rest}: SearchRuleConditions) => {
			setSearchRules((current) => ({...current, ...rest}));
			setSearchSort((current) => {
				const newSortField = _getCorrectedSortField(
					sortField ?? current.sortField,
					rest,
				);
				return {
					sortField: newSortField,
					sortOrder:
						_getCorrectedSortOrder(
							sortOrder, // ?? current.sortOrder,
							rest,
						) ?? current.sortOrder,
				};
			});
		},
	);

	useEffect(() => {
		const {sortField, sortOrder, ...rest} = conditions || {};
		setSearchRules((current) => ({...current, ...rest}));
		setSearchSort((current) => {
			const newSortField = _getCorrectedSortField(
				sortField ?? current.sortField,
				rest,
			);
			return {
				sortField: newSortField,
				sortOrder: sortOrder ?? current.sortOrder,
			};
		});
	}, [conditions]);

	useEffect(() => {
		if (!sortFieldPrefKey && !sortOrderPrefKey) return;
		const unsubs = [
			(() => {
				const listener = (pref: string) => {
					if (pref !== sortFieldPrefKey && pref !== sortOrderPrefKey) return;
					setSearchSort(
						getUserSort({
							sortFieldPrefKey,
							sortOrderPrefKey,
							defaultSortField: DEFAULT_SEARCH_SORT_FIELD,
							defaultSortOrder: DEFAULT_SEARCH_SORT_ORDER,
						}),
					);
				};
				userPrefs.on('change', listener);
				return () => userPrefs.off('change', listener);
			})(),
		];
		return () => {
			unsubs.forEach((unsub) => unsub());
		};
	}, [sortFieldPrefKey, sortOrderPrefKey]);

	return {
		searchRulesAtom: searchRulesAtom.current,
		searchRulesValidAtom: searchRulesValidAtom.current,
		searchSortAtom: searchSortAtom.current,
		setSearchRules,
		setSearchSort,
		updateSearchRulesWithoutChangingPreferences,
	};
};
